import Filter from '@/src/components/organism/Filter';
import { useEffect, useRef, useState } from 'react';
import { bannerFilters } from './filter';
import { useQuery } from '@tanstack/react-query';
import useQueryValues from '@/src/hooks/useQueryValues';
import MainContentTitle from '@/src/components/molecule/MainContentTitle';
import { AgGridReact } from 'ag-grid-react';
import Table from '@/src/components/atom/Table';
import { bannerListColumn } from './column';
import SectionCardNoneTitle from '@/src/components/molecule/SectionCardNoneTitle';
import Typo from '@/src/components/atom/Typo';
import Button from '@/src/components/atom/Button';
import LucideIcon from '@/src/components/atom/Icons/LucideIcon';
import usePaginationQuery from '@/src/hooks/usePaginationQuery';
import { NAV_CONST } from '@/src/constants/nav';
import { Link, useNavigate } from 'react-router-dom';
import PATH from '@/src/routes/path';
import {
  getBannerAPI,
  getBannerContentCatetoryAPI,
} from '@/src/apis/rest/banner';
import Tabs from '@/src/components/molecule/Tabs';

export default function SubBannerListPage() {
  const navigate = useNavigate();

  const { defaultValues } = useQueryValues({
    createdAt: ['createdAtFrom', 'createdAtTo'],
  });
  const { page, pageSize, handlePageChange } = usePaginationQuery();
  const [bannerType, setBannerType] = useState<string>(
    (defaultValues as any)?.tabValue || 'home',
  );

  const homeGridRef = useRef<AgGridReact>(null);
  const donateGridRef = useRef<AgGridReact>(null);
  const communityGridRef = useRef<AgGridReact>(null);
  const challengeGridRef = useRef<AgGridReact>(null);
  const [values, setValues] = useState(defaultValues);

  // query
  const { data: bannerContentCategoryOptions } = useQuery({
    queryKey: ['banner-content-category-options'],
    queryFn: getBannerContentCatetoryAPI,
    select: data =>
      (data?.rows || []).map(item => ({ label: item.value, value: item.key })),
  });
  const {
    data: bannerListOrigin,
    isError,
    isFetching,
  } = useQuery({
    queryKey: ['banners', values, page, pageSize, bannerType],
    queryFn: () => {
      let bannerTypeResult: Partial<{
        bannerHomeShowedYn: boolean;
        bannerDonationShowedYn: boolean;
        bannerCommunityShowedYn: boolean;
        bannerChallengeShowedYn: boolean;
      }> = {};

      switch (bannerType) {
        case 'home':
          bannerTypeResult = {
            bannerHomeShowedYn: true,
          };
          break;
        case 'donate':
          bannerTypeResult = {
            bannerDonationShowedYn: true,
          };
          break;
        case 'community':
          bannerTypeResult = {
            bannerCommunityShowedYn: true,
          };
          break;
        case 'challenge':
          bannerTypeResult = {
            bannerChallengeShowedYn: true,
          };
          break;
        default:
          bannerTypeResult = {
            bannerHomeShowedYn: true,
          };
          break;
      }

      return getBannerAPI({
        ...values,
        ...bannerTypeResult,
        bannerType: '상세 배너',
        orderByKey: 'id',
        page,
        pageSize,
      });
    },
  });

  // effect

  useEffect(() => {
    if (isFetching && homeGridRef.current) {
      homeGridRef.current.api?.showLoadingOverlay();
    }
  }, [isFetching]);

  return (
    <>
      <MainContentTitle
        pageTitle={NAV_CONST.subBannerManagement}
        breadcrumbList={[NAV_CONST.subBanner]}
      />
      <div className="mb-20 flex flex-col gap-6">
        <Filter
          title="검색 필터"
          filters={bannerFilters(bannerContentCategoryOptions || [])}
          onSubmit={values => {
            handlePageChange(1, pageSize, false);
            setValues(values);
          }}
        />
        <div className="flex flex-col gap-y-6">
          <SectionCardNoneTitle
            contentProps={{
              className: 'flex flex-col gap-y-4',
            }}
          >
            <div className="flex items-center justify-between">
              <Typo typoType="b18m">서브 배너 목록</Typo>
              <div className="flex gap-2">
                <Link to={PATH.SUB_BANNER_CREATE}>
                  <Button buttonSize="medium">
                    <div className="flex items-center gap-x-2">
                      <LucideIcon name="Plus" color="white" />
                      서브 배너 등록
                    </div>
                  </Button>
                </Link>
              </div>
            </div>
            <Tabs
              defaultValue="home"
              onChange={e => setBannerType(e)}
              hasSearchParams
              includeValues={['home', 'donate', 'community', 'challenge']}
            >
              <div className="mb-4">
                <Tabs.TabsList>
                  <Tabs.SingleTrigger value="home" label="홈" />
                  <Tabs.SingleTrigger value="donate" label="기부하기" />
                  <Tabs.SingleTrigger value="community" label="커뮤니티" />
                  <Tabs.SingleTrigger value="challenge" label="챌린지" />
                </Tabs.TabsList>
              </div>

              <Tabs.SingleContent value="home">
                <Table
                  ref={homeGridRef}
                  rowData={isError ? [] : bannerListOrigin?.rows || []}
                  isPaginationDataMapping
                  columnDefs={bannerListColumn(e => {
                    navigate(PATH.SUB_BANNER_DETAIL.replace(':id', String(e)));
                  })}
                  total={bannerListOrigin?.count || 0}
                  page={page}
                  pageSize={pageSize}
                  handlePaginationClick={handlePageChange}
                />
              </Tabs.SingleContent>
              <Tabs.SingleContent value="donate">
                <Table
                  ref={donateGridRef}
                  rowData={isError ? [] : bannerListOrigin?.rows || []}
                  isPaginationDataMapping
                  columnDefs={bannerListColumn(e => {
                    navigate(PATH.SUB_BANNER_DETAIL.replace(':id', String(e)));
                  })}
                  total={bannerListOrigin?.count || 0}
                  page={page}
                  pageSize={pageSize}
                  handlePaginationClick={handlePageChange}
                />
              </Tabs.SingleContent>
              <Tabs.SingleContent value="community">
                <Table
                  ref={communityGridRef}
                  rowData={isError ? [] : bannerListOrigin?.rows || []}
                  isPaginationDataMapping
                  columnDefs={bannerListColumn(e => {
                    navigate(PATH.SUB_BANNER_DETAIL.replace(':id', String(e)));
                  })}
                  total={bannerListOrigin?.count || 0}
                  page={page}
                  pageSize={pageSize}
                  handlePaginationClick={handlePageChange}
                />
              </Tabs.SingleContent>
              <Tabs.SingleContent value="challenge">
                <Table
                  ref={challengeGridRef}
                  rowData={isError ? [] : bannerListOrigin?.rows || []}
                  isPaginationDataMapping
                  columnDefs={bannerListColumn(e => {
                    navigate(PATH.SUB_BANNER_DETAIL.replace(':id', String(e)));
                  })}
                  total={bannerListOrigin?.count || 0}
                  page={page}
                  pageSize={pageSize}
                  handlePaginationClick={handlePageChange}
                />
              </Tabs.SingleContent>
            </Tabs>
          </SectionCardNoneTitle>
        </div>
      </div>
    </>
  );
}
